import type { GridColDef } from '@mui/x-data-grid-pro'
import Button from 'ext-ui/components/Button/Button'
import ContentCard from 'ext-ui/components/ContentCard/ContentCard'
import {
  base_colorContentBorder,
  base_colorRaisedSurface,
  text_colorTextPrimary,
} from 'ext-ui/style/colors'
import {
  font_family_weight_regular,
  font_size_md,
} from 'ext-ui/style/typography'
import React, { ReactElement, ReactNode, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import DataGrid, {
  DATA_GRID_UNMEASURED_BORDER_HEIGHT,
  DataGridHeaderStyle,
  DataGridRowHeight,
} from 'src/components/DataGrid/DataGrid'
import TeamAvatarsIcon from 'src/components/TeamAvatarsIcon/TeamAvatarsIcon'
import UserAvatarAndName from 'src/components/UserAvatarAndName/UserAvatarAndName'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
// Shared with the stats roll-up and the recent-transactions panel so the tab has
// ONE navigation idiom and the modifier-click guard lives in one place.
import { isPlainLeftClick } from 'src/feature/CardOverviewStats/navigationClick'
import StatNavLink from 'src/feature/CardOverviewStats/StatNavLink'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import PluralTrns from 'src/localization/PluralTrns'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import type { UserListModel } from 'src/models/user.models'
import type { BudgetVirtualCardCounts } from 'src/queries/statistics/budgetVirtualCardCounts.queries'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { InfiniteScrollProps } from 'src/types'

import * as style from './CardBudgetsTable.style'

/** `DataGrid` hard-codes `columnHeaderHeight={44}`. */
const HEADER_HEIGHT = 44
/**
 * The line-height half of `font_md` (14px/18px). Restated as a literal because
 * `font_md` is a styled-components mixin and this is consumed from MUI's `sx`,
 * which takes plain values.
 */
const CELL_LINE_HEIGHT = '18px'
/**
 * `DataGrid` renders its grid at `height - 60`, an inset sized for the full-page
 * tables it was built for (toolbar + footer). The snapshot has neither — its
 * footer is hidden — so the inset is added back rather than left as dead space
 * under the last row.
 */
const DATA_GRID_HEIGHT_INSET = 60

/** The em dash a missing count or amount renders as, never a fabricated zero. */
const NO_VALUE = '—'

type CardBudgetsTableRow = {
  id: string
  budget: CreditCardListModel
  /** Active virtual cards on this budget; undefined until the counts land. */
  cardCount?: number
  /**
   * Whether the current user may open this budget's detail page.
   *
   * A budget can sit on this card while belonging to an org the user has not
   * joined (a pending invite, or they left an org that still has budgets here).
   * Legacy rendered an inline "Action Required" panel for that case; this drops
   * the panel but keeps the gate, so the row never navigates somewhere the
   * user cannot open.
   */
  canView: boolean
}

/**
 * What the third column — the one between Type and Owner — reports.
 *
 * `'cards'` is the card-detail design: the budget's active virtual-card count.
 * It is NOT free. The counts come from their own POST /statistics carrying one
 * COUNT definition per budget on screen, keyed on the whole loaded id set, so a
 * list that pages without bound re-sends every id it has already asked about
 * (see `budgetVirtualCardCounts.queries.ts`). On a card's own budgets that set
 * is small and the column is worth its request.
 *
 * `'budgetUser'` is the column the org-wide budgets list carries today, and it
 * costs NOTHING extra: `budgetUser` / `budgetUsers` both arrive on the same
 * GET /creditcards response the rows are already built from. Prefer it wherever
 * the budget count is unbounded.
 */
export type CardBudgetsTableMiddleColumn = 'cards' | 'budgetUser'

/**
 * Pairs the middle column with the data it needs, so a caller cannot ask for
 * the Cards column without supplying counts — nor pay for counts it will not
 * show.
 */
type CardBudgetsTableMiddleColumnProps =
  | {
      middleColumn?: 'cards'
      /**
       * Budget id → active virtual-card count. A budget missing from the map
       * renders an em dash; the column is never withheld and never guesses a
       * zero.
       */
      cardCounts: BudgetVirtualCardCounts
    }
  | {
      middleColumn: 'budgetUser'
      cardCounts?: undefined
    }

/**
 * Everything that does NOT vary with the middle column. Exported so callers and
 * tests can name the stable half of the props without destructuring the union.
 */
export type CardBudgetsTableBaseProps = {
  className?: string
  /** The budgets to render, already filtered and ordered by the caller. */
  budgets: CreditCardListModel[]
  /** True while the first page of budgets is in flight. */
  isPending: boolean
  /** True when the budget list request failed. */
  isError?: boolean
  /**
   * The count shown opposite the heading ("2 budgets"). Omitted on the Overview
   * snapshot, which carries a "view all" link in that slot instead.
   */
  totalCount?: number
  /**
   * Destination for the heading's "view all" link, built by the mounting page —
   * features do not know URLs. Omitted when there is no Budgets tab to link to,
   * which leaves the heading intact and drops only the link.
   */
  viewAllHref?: string
  /** SPA navigation, supplied by the page. See `StatNavLink`. */
  onNavigate?: (href: string) => void
  /**
   * Builds a row's destination. Supplied by the page — features do not know
   * URLs — so the budget-name cell can be a real `<a href>` rather than a
   * click handler.
   *
   * That is what gives rows ⌘-click / middle-click / "open in new tab",
   * right-click → copy link, a hover status-bar preview, and a place in a
   * screen reader's link list. `onSelectBudget` still handles the plain click
   * anywhere else in the row.
   */
  budgetHref: (budgetId: string) => string
  /** Selecting a row. The page decides what that means (navigate to the budget). */
  onSelectBudget: (budgetId: string) => void
  /**
   * Starting the create-budget flow. Omitted where that flow is unavailable —
   * a card that cannot issue budgets, or a user who must set up an org first —
   * which drops the CTA rather than offering a dead end. The page owns that
   * decision because the answer is account-level, not table-level.
   */
  onCreateBudget?: () => void
  /**
   * Paging state for the full Budgets tab. Omitted on the Overview snapshot,
   * which renders a fixed three rows and never pages.
   */
  infiniteScrollProps?: InfiniteScrollProps<Record<string, never>>
  /**
   * Explicit grid height for the full Budgets tab, which fills the viewport.
   * Omitted on the snapshot, which sizes itself to its rows.
   */
  height?: number
  /**
   * What to render in place of the grid when there are no budgets to show.
   *
   * Supplied by the page rather than chosen here, because the honest empty
   * state depends on things a table cannot see: whether filters are applied
   * ("no budgets" vs "no budgets match your search"), and whether this user may
   * create one — which is the difference between an illustration with a Create
   * Budget CTA and the same illustration without it.
   *
   * Passing `<TableEmptyState>` is also what keeps this component routing-free:
   * that component builds `to` destinations, and the feature boundary forbids
   * URL knowledge here.
   *
   * Omitted falls back to a plain sentence, which is what the Overview snapshot
   * wants — a three-row panel has no room for an illustration.
   */
  emptyState?: ReactNode
  /**
   * Replaces the derived "N budgets" text opposite the heading.
   *
   * For a list whose count needs saying differently — the org-wide list reports
   * "5 Results (of 27 Budgets)" once filters are applied, which `totalCount`
   * alone cannot express. Ignored on the Overview snapshot, whose heading slot
   * carries the "view all" link instead.
   */
  countLabel?: ReactNode
  /**
   * Extra controls for the end of the heading row, after the count and the
   * New budget CTA.
   *
   * This is where the org-wide list puts its Filters button. It lives in the
   * heading rather than in a bar above the card so the page is ONE surface: a
   * separate filter block above meant two stacked cards, two headings and two
   * type scales for one table.
   */
  headerActions?: ReactNode
}

export type CardBudgetsTableProps = CardBudgetsTableBaseProps &
  CardBudgetsTableMiddleColumnProps

/**
 * The redesigned budget table for the card-detail page (EX-40737).
 *
 * ONE component for both places the design shows it — the Overview tab's
 * three-row snapshot and the Budgets tab's full list — because they are the same
 * table under two headings. The differences are all props: the snapshot passes
 * `viewAllHref` and sizes itself to its rows, the full list passes `totalCount`
 * and `infiniteScrollProps` and takes an explicit height.
 *
 * Columns, in design order: Budget (card-type glyph + name) · Type · Cards ·
 * Owner · Issued / limit. That last pair replaces legacy's separate Exposure and
 * Remaining columns, which reported the same money in a shape the design drops.
 *
 * The THIRD column is the one that varies — see
 * {@link CardBudgetsTableMiddleColumn}. Cards is the card-detail design and
 * costs a per-page statistics request; Budget User is what an unbounded list
 * should carry instead, and costs nothing beyond the rows themselves.
 *
 * Routing-free by the `src/feature/**` boundary: the page supplies the "view
 * all" destination and the row-selection handler.
 */
export default function CardBudgetsTable(
  props: CardBudgetsTableProps,
): ReactElement {
  const {
    className,
    budgets,
    cardCounts,
    middleColumn = 'cards',
    isPending,
    isError = false,
    totalCount,
    viewAllHref,
    onNavigate,
    budgetHref,
    onSelectBudget,
    onCreateBudget,
    infiniteScrollProps,
    height,
    emptyState,
    countLabel,
    headerActions,
  } = props

  const translate = useTranslate()
  const currencyFormatter = useCurrencyFormatter()
  const users = useSelector(selectUserListModelsFromState)
  const currentUser = useCurrentUser()

  const rows = useMemo(
    (): CardBudgetsTableRow[] =>
      budgets.map((budget) => ({
        id: budget.id,
        budget,
        cardCount: cardCounts?.[budget.id],
        // The same expression all three legacy budget tables compute
        // (BudgetsTableLarge, BudgetsTableSmall, BudgetDataGrid).
        canView:
          budget.organizationId === currentUser?.organizationId ||
          budget.ownerId === currentUser?.id,
      })),
    [budgets, cardCounts, currentUser?.organizationId, currentUser?.id],
  )

  /**
   * The account owner for a row.
   *
   * Reads the `owner` the list response already carries, and treats the Redux
   * users map as a FALLBACK rather than the source. That map is filled by a
   * separate paginated `getUsers`, so on an org-wide budget list — where owners
   * routinely sit outside whatever page of users happens to be loaded — looking
   * there first left the cell blank for a user the row's own payload named.
   *
   * The fallback stays for the surfaces whose budgets are normalized through
   * older paths that drop `owner`.
   */
  const ownerOf = useCallback(
    (budget: CreditCardListModel): UserListModel | undefined =>
      budget.owner ?? users[budget.ownerId],
    [users],
  )

  /** The single budget user for a row. Same precedence as {@link ownerOf}. */
  const budgetUserOf = useCallback(
    (budget: CreditCardListModel): UserListModel | undefined =>
      budget.budgetUser ?? users[budget.userId],
    [users],
  )

  /**
   * "$1,100.00 / $20,000.00". A budget with no issuing limit reads "Unlimited"
   * on the right of the slash rather than an invented ceiling; that is the same
   * word legacy's Remaining column used for the same state.
   *
   * The left half is `activeIssuedAmount` — `SUM(limitCents)` over the budget's
   * ACTIVE virtual cards, i.e. what is issued and still live. Two neighbours
   * are one step away and are not this:
   *
   *  - `totalActiveVirtualCardBalanceCents`, what legacy's Exposure column
   *    shows, is `SUM(balanceCents)` over the same cards — issued MINUS what
   *    has been spent on them. A live card funded $10 with $3 spent has $10
   *    issued and $7 exposure, so exposure would understate a column labelled
   *    Issued by exactly the spend.
   *  - `issuedAmountCents` is the enforcement ledger, which additionally
   *    retains what CLOSED cards spent (`rebalanceIssuerBudget` returns only a
   *    cancelled card's unspent balance). That figure satisfies
   *    `issued + remaining = limit`, but it counts cards that are gone.
   *
   * This column deliberately reports only live cards. The trade is that it no
   * longer complements the budget detail page's "Remaining": a budget whose
   * cards have all closed reads $0.00 here while its Remaining is below its
   * limit, because the difference is spend the budget cannot get back. That is
   * accepted — the question this column answers is "what is issued right now",
   * not "how much of the limit is used up".
   *
   * Settled spend is not shown and cannot be, per cycle: every backend spend
   * aggregate is `SUM(...) withActiveStatus` so a closed card reports 0, and
   * deriving it from the ledger collapses after a recur. That needs
   * cycle-bounded transaction data, a request per row.
   */
  const issuedOverLimit = useCallback(
    (budget: CreditCardListModel): string => {
      const issued = currencyFormatter({
        amount: budget.activeIssuedAmount,
        currency: budget.currency,
      })
      const limit =
        budget.issuingLimit !== undefined
          ? currencyFormatter({
              amount: budget.issuingLimit,
              currency: budget.currency,
            })
          : translate('Unlimited')
      return `${issued} / ${limit}`
    },
    [currencyFormatter, translate],
  )

  /**
   * Supplies the header element instead of letting MUI render its own — see
   * `style.columnHeader` for why restyling MUI's is a cascade race. `headerName`
   * stays set alongside it, because that is what MUI reads for the column's
   * accessible name.
   */
  const renderHeader = useCallback(
    (label: string, align?: 'right') => (): ReactElement => (
      <span
        css={align === 'right' ? style.columnHeaderRight : style.columnHeader}
      >
        {label}
      </span>
    ),
    [],
  )

  const columns = useMemo(
    (): Array<GridColDef<CardBudgetsTableRow>> => [
      {
        field: 'budgetName',
        headerName: translate('Budget'),
        renderHeader: renderHeader(translate('Budget')),
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderCell: (params): ReactElement => {
          /*
            Decorative glyph: it encodes recurring-vs-standard and
            active-vs-inactive, both of which the Type column and the row's own
            state already say in words. Alt text would double-announce.
          */
          const content = (
            <>
              <img
                css={style.nameGlyph}
                src={params.row.budget.cardTypeImageSrc}
                alt=""
              />
              <span css={style.nameText}>
                {params.row.budget.truncatedDisplayName}
              </span>
            </>
          )

          // No anchor for a budget the user cannot open — a link that
          // dead-ends is worse than plain text.
          if (!params.row.canView) {
            return <span css={style.nameCellStatic}>{content}</span>
          }

          return (
            <a
              css={style.nameLink}
              href={budgetHref(params.row.budget.id)}
              onClick={(event): void => {
                // Modified clicks and non-primary buttons fall through to the
                // browser, so open-in-new-tab still works. Shared with
                // StatNavLink so the two cannot drift.
                if (!isPlainLeftClick(event)) return
                event.preventDefault()
                onSelectBudget(params.row.budget.id)
              }}
            >
              {content}
            </a>
          )
        },
      },
      {
        field: 'type',
        headerName: translate('Type'),
        renderHeader: renderHeader(translate('Type')),
        minWidth: 116,
        flex: 0.4,
        sortable: false,
        // `recurs` is the same field `cardTypeImageSrc` picks its glyph from, so
        // the word and the icon cannot disagree.
        //
        // `valueGetter` stays alongside `renderCell`: it is what MUI reads for
        // the cell's title tooltip and its accessible value.
        valueGetter: (_value, row): string =>
          row.budget.recurs ? translate('Recurring') : translate('Standard'),
        renderCell: (params): ReactElement => (
          <span css={style.textCell}>
            {params.row.budget.recurs
              ? translate('Recurring')
              : translate('Standard')}
          </span>
        ),
      },
      middleColumn === 'cards'
        ? {
            field: 'cards',
            headerName: translate('Cards'),
            renderHeader: renderHeader(translate('Cards')),
            minWidth: 80,
            flex: 0.25,
            sortable: false,
            // Em dash, not 0: the counts arrive in their own request, and a
            // budget whose count has not landed (or whose request failed) has
            // an unknown number of cards, not none.
            valueGetter: (_value, row): string =>
              row.cardCount === undefined ? NO_VALUE : String(row.cardCount),
            renderCell: (params): ReactElement => (
              <span css={style.textCell}>
                {params.row.cardCount === undefined
                  ? NO_VALUE
                  : String(params.row.cardCount)}
              </span>
            ),
          }
        : {
            field: 'budgetUser',
            headerName: translate('Budget User'),
            renderHeader: renderHeader(translate('Budget User')),
            // Sized like Owner rather than like Cards: it holds the same
            // avatar-plus-name lockup, and on a team budget an avatar stack
            // and a pill besides.
            minWidth: 180,
            flex: 0.7,
            sortable: false,
            renderCell: (params): ReactElement | null => {
              const { budgetUsers } = params.row.budget

              /*
                A budget with SEVERAL users cannot be reduced to one name, so it
                gets the stacked avatars and the "Team Budget" pill — the same
                treatment, from the same component, as the legacy budgets list.
              */
              if (budgetUsers !== undefined && budgetUsers.length > 1) {
                return <TeamAvatarsIcon users={budgetUsers} showPill />
              }

              const budgetUser = budgetUserOf(params.row.budget)
              return budgetUser !== undefined ? (
                <UserAvatarAndName user={budgetUser} />
              ) : null
            },
          },
      {
        field: 'owner',
        headerName: translate('Owner'),
        renderHeader: renderHeader(translate('Owner')),
        minWidth: 180,
        flex: 0.7,
        sortable: false,
        renderCell: (params): ReactElement | null => {
          const owner = ownerOf(params.row.budget)
          return owner !== undefined ? <UserAvatarAndName user={owner} /> : null
        },
      },
      {
        field: 'issuedLimit',
        headerName: translate('Issued / limit'),
        renderHeader: renderHeader(translate('Issued / limit'), 'right'),
        minWidth: 150,
        flex: 0.6,
        sortable: false,
        // Terminal column, right-aligned per the design. `align`/`headerAlign`
        // rather than CSS so MUI keeps the cell and its header in step.
        align: 'right',
        headerAlign: 'right',
        renderCell: (params): ReactElement => (
          <span css={style.amountCell}>
            {issuedOverLimit(params.row.budget)}
          </span>
        ),
      },
    ],
    [
      translate,
      middleColumn,
      ownerOf,
      budgetUserOf,
      issuedOverLimit,
      renderHeader,
      budgetHref,
      onSelectBudget,
    ],
  )

  /**
   * The full Budgets tab, as opposed to the Overview snapshot. Paging is the
   * honest discriminator: only the full list supplies it, and it is exactly the
   * property that decides whether the grid must virtualize and size itself
   * against a scrollbar.
   */
  const isPaged = infiniteScrollProps !== undefined

  const isEmpty = !isPending && !isError && rows.length === 0

  /**
   * Exactly the height the loaded rows occupy.
   *
   * `DATA_GRID_UNMEASURED_BORDER_HEIGHT` is not slack — see its definition. Two
   * borders sit outside MUI's height model (this table's header hairline being
   * one of them), so without it the rows overflow the viewport by 2px and MUI
   * paints a vertical scrollbar down a snapshot that shows at most three rows.
   */
  const contentHeight =
    HEADER_HEIGHT +
    rows.length * DataGridRowHeight.Default +
    DATA_GRID_HEIGHT_INSET +
    DATA_GRID_UNMEASURED_BORDER_HEIGHT

  /**
   * The snapshot always sizes to its rows. The full tab asks for a
   * viewport-filling height, which is right while the list can still grow and
   * wrong once it cannot — a card with four budgets got a table with a screen's
   * worth of blank space under the last row.
   *
   * Clamped ONLY once the list is complete. Shrinking to content while more
   * pages exist would stop the scroller from overflowing, and the scroll that
   * never happens is the one that fetches page two — the list would wedge on
   * whatever it had loaded.
   */
  const listComplete =
    infiniteScrollProps !== undefined &&
    infiniteScrollProps.complete &&
    infiniteScrollProps.hasNextPage !== true

  const gridHeight =
    height === undefined
      ? contentHeight
      : listComplete
        ? Math.min(height, contentHeight)
        : height

  const body = (): ReactElement => {
    if (isPending) {
      return (
        <p css={style.statusBody} role="status">
          {translate('Loading budgets')}
        </p>
      )
    }

    if (isError) {
      return (
        <p css={style.statusBody} role="alert">
          {translate("We couldn't load budgets. Try again later.")}
        </p>
      )
    }

    if (isEmpty) {
      // The page's illustrated empty state when it supplied one; otherwise the
      // one-line fallback the Overview snapshot is sized for.
      return emptyState !== undefined ? (
        <>{emptyState}</>
      ) : (
        <p css={style.statusBody}>
          {translate('No budgets on this card yet.')}
        </p>
      )
    }

    return (
      <DataGrid<Record<string, never>, CardBudgetsTableRow>
        css={style.grid}
        rows={rows}
        columns={columns}
        filtersBody={{}}
        rowHeight={DataGridRowHeight.Default}
        // The design's header is a plain kicker row over a hairline, not the
        // shared grid's filled band.
        headerStyle={DataGridHeaderStyle.NoBackground}
        height={gridHeight}
        infiniteScrollProps={infiniteScrollProps}
        // Fixed column set, and all of it has to be said explicitly —
        // DataGridPro ships drag-reorder and resize on by default.
        // `disableColumnMenu` subsumes hiding and is the only one that removes
        // the ⋮ entirely, which matters because every column here is
        // `sortable: false` and a sort-only menu would open empty.
        disableColumnMenu
        disableColumnReorder
        disableColumnResize
        pinFirstColumn={false}
        checkboxSelection={false}
        // No pagination and no selection, so MUI's default footer contributes a
        // row total and ~52px of chrome that says nothing the heading doesn't.
        hideFooter
        // Only for the fixed-height snapshot, whose handful of rows never need
        // windowing and where virtualization is what makes the scroller's
        // measured height wobble as it settles — which is what makes page
        // scroll catch on the table.
        //
        // The full Budgets tab keeps it ON. That list pages without bound, and
        // an unvirtualized grid both renders every loaded row and mismeasures
        // its own width once a vertical scrollbar appears — which is what pushed
        // the terminal column under the scrollbar gutter.
        disableVirtualization={!isPaged}
        // ⚠️ `DataGrid` merges consumer `sx` with a SHALLOW spread, so any key
        // here REPLACES its rule rather than extending it. `.MuiDataGrid-cell`
        // is the one key below that the shared grid also sets, so its three
        // declarations are restated verbatim — dropping them collapses every
        // cell's flex centring and re-adds padding the row heights assume.
        //
        // These live in `sx` rather than the `css` prop because MUI injects its
        // own rules through emotion on the grid root; a styled-components
        // descendant selector is a specificity race against them, and `sx` is
        // the channel that reliably wins.
        sx={{
          // No resizing and no reordering here, so the grab handles the shared
          // grid keeps at `opacity: 1` have nothing to grab — they only read as
          // column dividers, which the design does not have.
          '& .MuiDataGrid-columnSeparator': { display: 'none' },
          // The shared grid insets headers by 16px while leaving cells at 0, so
          // every header sat a notch right of the column it labels. The design
          // aligns them, and the cells are the ones already flush with the
          // panel's content edge.
          '& .MuiDataGrid-columnHeader': {
            padding: 0,
            // ↓ restated from the shared grid — see the shallow-merge note.
            // The background matters: on the paged list the header is sticky
            // over scrolling rows, and a transparent one lets them show through.
            backgroundColor: base_colorRaisedSurface,
            borderLeftStyle: 'unset',
          },
          // One hairline under the whole header row; the shared grid marks the
          // band with a background fill instead, which NoBackground removes.
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: `1px solid ${base_colorContentBorder}`,
          },
          // The scrollbar gutter's filler cell carries a left border, which
          // reads as a stray rule floating past the last column.
          '& .MuiDataGrid-scrollbarFiller, & .MuiDataGrid-filler': {
            borderLeft: 'none',
          },
          // The snapshot is sized to exactly fit its rows, so it is never meant
          // to scroll — but the header's hairline and sub-pixel row heights
          // leave a pixel or two of scrollable range, which paints a scrollbar
          // and makes the scroller swallow wheel events the page should get.
          // The same fix, for the same reason, as the recent-transactions panel.
          //
          // The PAGED list must keep scrolling, so it inherits the shared rule
          // untouched.
          ...(isPaged
            ? {}
            : {
                /*
                  The vertical scrollbar itself, belt-and-braces over the height
                  arithmetic above.

                  In MUI X v8 this is a separate absolutely-positioned overlay
                  element, not the virtual scroller — which is why the
                  `overflowY: 'hidden'` below never suppressed it. The snapshot
                  is sized to exactly fit its rows and can never have anywhere
                  to scroll, so any range MUI finds is chrome it mismeasured,
                  and the honest response is to not draw the control.

                  The PAGED list genuinely scrolls, so it must never get this.
                */
                '& .MuiDataGrid-scrollbar--vertical': { display: 'none' },
                '& .MuiDataGrid-virtualScroller': {
                  // ↓ restated from the shared grid — see the shallow-merge
                  // note. Losing `overscrollBehaviorX` in particular would let
                  // a horizontal trackpad swipe trigger browser BACK.
                  willChange: 'transform',
                  transform: 'translateZ(0)',
                  contain: 'layout paint',
                  backfaceVisibility: 'hidden',
                  overscrollBehaviorX: 'none',
                  // The shared grid `contain`s vertical overscroll so a tall,
                  // genuinely scrollable table does not scroll the page out
                  // from under it. This one is never scrollable, so that only
                  // gets in the way.
                  overscrollBehaviorY: 'auto',
                  overflowY: 'hidden',
                },
              }),
          '& .MuiDataGrid-cell': {
            // ↓ restated from the shared grid — see the shallow-merge note.
            display: 'flex',
            alignItems: 'center',
            padding: 0,
            // Body cells sit at 14px regular; the default inherits a larger,
            // heavier face from the page.
            fontSize: font_size_md,
            fontFamily: font_family_weight_regular,
            color: text_colorTextPrimary,
            // `line-height` is NOT optional here, and it is the one the shared
            // grid never had to set. Its own `DataGridCell` applies `font_md`
            // (14px/18px) to the text it renders; these cells are plain
            // elements, so without this they inherit the page's much taller
            // line box. That is what dropped the name in `UserAvatarAndName`
            // below its avatar — the component row-aligns on `flex-start`, so
            // a tall line box centres the glyphs well under the picture.
            lineHeight: CELL_LINE_HEIGHT,
          },
        }}
        onRowClick={(params, event): void => {
          /*
            The same guard the name-cell anchor uses, and it has to be here too.
            A ⌘-click on that anchor deliberately does NOT preventDefault — it
            lets the browser open a new tab — but the event still bubbles to
            this handler. Checking only `defaultPrevented` would navigate the
            CURRENT tab as well, so the user gets both.

            It also covers the plain-click case: the anchor preventDefaults and
            handles it, so this sees `defaultPrevented` and stays out of the way.
          */
          if (!isPlainLeftClick(event)) return
          if (!params.row.canView) return
          onSelectBudget(params.row.budget.id)
        }}
        // Row navigation must not be mouse-only (src/CLAUDE.md WCAG AA floor).
        // MUI gives cells a roving tabindex, so Enter on the focused cell is the
        // keyboard equivalent of clicking its row.
        onCellKeyDown={(params, event): void => {
          if (event.key !== 'Enter' || event.defaultPrevented) return
          if (!params.row.canView) return
          event.defaultMuiPrevented = true
          onSelectBudget(params.row.budget.id)
        }}
      />
    )
  }

  return (
    <ContentCard
      className={className}
      css={style.panel}
      data-testid="cardBudgetsTable"
    >
      <div css={style.header}>
        <h2 css={style.title}>{translate('Budgets')}</h2>
        <div css={style.headerActions}>
          {viewAllHref !== undefined && (
            <StatNavLink href={viewAllHref} onNavigate={onNavigate}>
              {/* Arrow lives in the source string, matching the sibling
                  CardOverviewStats links ("View budgets →", "Adjust limit →"). */}
              {translate('View all budgets →')}
            </StatNavLink>
          )}
          {viewAllHref === undefined && countLabel !== undefined && (
            <span css={style.count}>{countLabel}</span>
          )}
          {viewAllHref === undefined &&
            countLabel === undefined &&
            totalCount !== undefined && (
              <span css={style.count}>
                {/* A card with exactly one budget is reachable here — the
                  Overview snapshot takes the viewAllHref branch, so this count
                  only ever renders on the Budgets tab. */}
                <PluralTrns
                  pluralSource={'${count} budgets'}
                  singleSource={'1 budget'}
                  count={totalCount}
                />
              </span>
            )}
          {/*
            The design's New budget CTA. Rendered only when the page supplies a
            handler, which is how a card that cannot issue budgets — or a user
            whose create flow needs an org set up first — gets the heading
            without a button that would dead-end them.
          */}
          {onCreateBudget !== undefined && (
            <Button
              variant="secondary"
              onClick={onCreateBudget}
              data-testid="cardBudgetsTable-createBudget"
            >
              {translate('New budget')}
            </Button>
          )}
          {headerActions}
        </div>
      </div>
      {body()}
    </ContentCard>
  )
}
